/**
 * Asset queries and mutations.
 *
 * Each mutation seeds the detail cache from its own response and invalidates
 * the lists — the same rule the pilot and customer queries follow. Lists are
 * invalidated rather than patched because a saved change can move an asset in
 * or out of the `pilotReady` and `blocked` filters.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.4
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AssetDetail,
  AssetMap,
  CreateAssetRequest,
  ListAssetsQuery,
  UpdateAssetRequest,
} from '@nexus/contracts';

import { queryKeys } from '../../../lib/query-keys';

import * as assetsApi from './assets.api';
import type { AssetPage } from './assets.api';

export function useAssets(query: ListAssetsQuery): UseQueryResult<AssetPage> {
  return useQuery({
    queryKey: queryKeys.assets.list(query),
    queryFn: () => assetsApi.listAssets(query),
  });
}

/** The asset map (F19). */
export function useAssetMap(): UseQueryResult<AssetMap> {
  return useQuery({
    queryKey: queryKeys.assets.map(),
    queryFn: () => assetsApi.getAssetMap(),
  });
}

export function useAsset(id: string): UseQueryResult<AssetDetail> {
  return useQuery({
    queryKey: queryKeys.assets.detail(id),
    queryFn: () => assetsApi.getAsset(id),
  });
}

/** Seeds the detail cache and invalidates the lists. */
function useApplyAsset(): (asset: AssetDetail) => void {
  const queryClient = useQueryClient();

  return (asset: AssetDetail) => {
    queryClient.setQueryData(queryKeys.assets.detail(asset.id), asset);
    void queryClient.invalidateQueries({ queryKey: queryKeys.assets.lists() });
  };
}

export function useCreateAsset(): UseMutationResult<
  AssetDetail,
  Error,
  CreateAssetRequest
> {
  const apply = useApplyAsset();

  return useMutation({
    mutationFn: (body: CreateAssetRequest) => assetsApi.createAsset(body),
    onSuccess: apply,
  });
}

export function useUpdateAsset(
  id: string,
): UseMutationResult<AssetDetail, Error, UpdateAssetRequest> {
  const apply = useApplyAsset();

  return useMutation({
    mutationFn: (body: UpdateAssetRequest) => assetsApi.updateAsset(id, body),
    onSuccess: apply,
  });
}
